import { BaseNodeCommand, NodeChangeRecord } from '../base/BaseNodeCommand';
import type { Node } from '../../models/scene';

/**
 * Command to change the color of one or more nodes
 * Follows proper command pattern with reversible mutations
 */
export class ChangeNodeColorCommand extends BaseNodeCommand {
  private nodeIds: string[];
  private newColor: string;
  
  constructor(nodeIds: string[], newColor: string) {
    super('Change Node Color', `Change color to ${newColor}`);
    this.nodeIds = nodeIds;
    this.newColor = newColor;
  }
  
  protected doExecute(): NodeChangeRecord[] {
    // Prepare batch updates
    const batchUpdates = this.nodeIds.map(nodeId => ({
      nodeId,
      changes: {
        fillColor: this.newColor
      } as Partial<Node>
    }));
    
    // Apply all updates in a single batch transaction
    if (batchUpdates.length > 1) {
      const batchRecords = (this.getMutationManager() as any).applyBatchNodeChanges(batchUpdates);
      return batchRecords || [];
    } else if (batchUpdates.length === 1) {
      const record = this.getMutationManager().applyNodeChange(batchUpdates[0].nodeId, batchUpdates[0].changes);
      return record ? [record] : [];
    }
    
    return [];
  }
  
  canMergeWith(other: any): boolean {
    // Can merge if it's the same type of command on the same nodes
    if (!(other instanceof ChangeNodeColorCommand)) return false;
    
    const otherCommand = other as ChangeNodeColorCommand;
    
    // Check if same nodes are being colored
    const thisIds = [...this.nodeIds].sort().join(',');
    const otherIds = [...otherCommand.nodeIds].sort().join(',');
    
    return thisIds === otherIds;
  }
  
  mergeWith(other: any): ChangeNodeColorCommand {
    const otherCommand = other as ChangeNodeColorCommand;
    
    // When merging, keep the original nodeIds but use the new color
    // This preserves the original state for undo
    const merged = new ChangeNodeColorCommand(this.nodeIds, otherCommand.newColor);
    merged.changeRecords = this.changeRecords; // Preserve original change records
    
    return merged;
  }
}